import React, { FunctionComponent, useMemo, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Enrollment } from '@spike/membership-model';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import Button from 'components/UI/V3/Button/Button';
import { CounterField } from 'components/UI/V3/CounterField/CounterField';
import useApiClientWrapper from 'hooks/useApiClientWrapper';

interface AdjustUsageDrawerProps {
    enrollment: Enrollment;
    onClose: () => void;
    onSaved: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        itemList: {
            borderRadius: 0,
            overflow: 'hidden',
            marginTop: 16
        },
        tableHeader: {
            display: 'grid',
            gridTemplateColumns: '1fr 56px 148px',
            alignItems: 'center',
            padding: '10px 0px',
            borderBottom: '1px solid #000'
        },
        tableHeaderLabel: {
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.4px'
        },
        tableHeaderCenter: {
            textAlign: 'center'
        },
        tableHeaderRight: {
            textAlign: 'right'
        },
        itemRow: {
            'display': 'grid',
            'gridTemplateColumns': '1fr 56px 148px',
            'alignItems': 'center',
            'padding': '14px 0px',
            'borderBottom': '1px solid #000',
            '&:last-child': {
                borderBottom: 'none'
            }
        },
        itemName: {
            fontSize: 15,
            fontWeight: 500,
            lineHeight: 1.2
        },
        itemType: {
            fontSize: 13,
            color: '#7A7A7A',
            marginTop: 2
        },
        itemQty: {
            textAlign: 'center',
            fontSize: 14,
            color: '#555'
        },
        itemUsage: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 4
        },
        counterOverride: {
            'alignSelf': 'flex-end',
            'gap': '10px !important',
            '& label': {
                minWidth: 'auto !important'
            },
            '& button': {
                fontSize: '16px !important'
            }
        },
        availBadge: {
            fontSize: 12,
            background: '#F4F4F4',
            borderRadius: 6,
            padding: '2px 8px',
            color: '#555',
            marginRight: 3
        }
    })
);

const AdjustUsageDrawer: FunctionComponent<AdjustUsageDrawerProps> = ({ enrollment, onClose, onSaved }) => {
    const classes = useStyles();
    const apiClientWrapper = useApiClientWrapper();

    const finiteItems = enrollment.membership.membershipItems.filter(item => !item.unlimited && item.id !== undefined);

    // Redeems that already happened (bookings/sales) — cannot be undone by the user.
    // actual_redeems = total_consumed - consumed_preset_offset
    const actualRedeems = useMemo(
        () =>
            finiteItems.reduce((acc, item) => {
                const key = String(item.id);
                const presetOffset = enrollment.consumedPreset?.[key] ?? 0;
                const totalConsumed = typeof item.consumed === 'number' ? item.consumed : 0;
                acc[key] = Math.max(0, totalConsumed - presetOffset);
                return acc;
            }, {} as Record<string, number>),
        []
    );

    // Initialize from item.consumed (total used = redeems + preset) to match the details view
    const [localValues, setLocalValues] = useState<Record<string, number>>(() =>
        finiteItems.reduce((acc, item) => {
            const key = String(item.id);
            acc[key] = typeof item.consumed === 'number' ? item.consumed : 0;
            return acc;
        }, {} as Record<string, number>)
    );

    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            const url = `/enrollments/${enrollment.id}?marketplace_id=${apiClientWrapper.marketplaceId}`;
            // Derive consumed_preset from the adjusted total: preset = total_used - actual_redeems
            const consumedPresetPayload = finiteItems.reduce((acc, item) => {
                const key = String(item.id);
                acc[key] = (localValues[key] ?? 0) - (actualRedeems[key] ?? 0);
                return acc;
            }, {} as Record<string, number>);
            await apiClientWrapper.apiClient.patch(
                url,
                { enrollment: { consumed_preset: consumedPresetPayload } },
                apiClientWrapper.requestConfig
            );
            onSaved();
            onClose();
        } catch (err) {
            console.error('Error saving usage adjustment', err);
        } finally {
            setSaving(false);
        }
    };

    if (finiteItems.length === 0) return null;

    return (
        <Drawer onClose={onClose}>
            <DrawerHeader title="Adjust Usage" />
            <DrawerBody>
                <Box className={classes.itemList}>
                    <Box className={classes.tableHeader}>
                        <Typography className={classes.tableHeaderLabel}>Item</Typography>
                        <Typography className={`${classes.tableHeaderLabel} ${classes.tableHeaderCenter}`}>
                            Qty
                        </Typography>
                        <Typography className={`${classes.tableHeaderLabel} ${classes.tableHeaderRight}`}>
                            Usage
                        </Typography>
                    </Box>
                    {finiteItems.map(item => {
                        const key = String(item.id);
                        const consumed = localValues[key] ?? 0;
                        const avail = item.quantity - consumed;
                        const minUsed = actualRedeems[key] ?? 0;
                        return (
                            <Box
                                key={key}
                                className={classes.itemRow}
                            >
                                <Box>
                                    <Typography className={classes.itemName}>{item.itemName}</Typography>
                                    <Typography className={classes.itemType}>{item.itemType}</Typography>
                                </Box>
                                <Typography className={classes.itemQty}>{item.quantity}</Typography>
                                <Box className={classes.itemUsage}>
                                    <CounterField
                                        value={consumed}
                                        minLimit={0}
                                        maxLimit={item.quantity}
                                        hasBorder={false}
                                        className={classes.counterOverride}
                                        onChange={value => setLocalValues(prev => ({ ...prev, [key]: value }))}
                                    />
                                    <Typography className={classes.availBadge}>Avail: {avail}</Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </DrawerBody>
            <DrawerFooter>
                <Button
                    size="large"
                    label="Save"
                    variant="black"
                    fullWidth
                    isLoading={saving}
                    disabled={saving}
                    onClick={handleSave}
                />
            </DrawerFooter>
        </Drawer>
    );
};

export default AdjustUsageDrawer;
